import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    type WorkQueueCase,
    type OutgoingAction,
    advanceWorkflow,
    fetchCase
} from '../../services/workQueue';
import { useToast } from '../../providers/ToastProvider';
import { reportError } from '../../services/errorReporting';
import { invalidateCaseDetail } from './util';
import { RichEditor, tiptapDocToPlainText } from '@odo/shared-ui';
import type { TiptapDoc } from '@odo/shared-ui';
import { uploadInternalImage } from '../../services/internalImages';
import { useEscapeKey } from '../../hooks/useEscapeKey';

const MAX_HOLD_NOTE_LENGTH = 2000;
const EMPTY_DOC: TiptapDoc = { type: 'doc', content: [] };

type HoldReason =
    | 'records-incomplete'
    | 'clinical-question'
    | 'in-office-step'
    | 'refinement-needed'
    | 'retention-needed';

interface ReasonMeta {
    id: HoldReason;
    label: string;
    help: string;
    /** Heading shown above the note field on step 2. */
    field: string;
    placeholder: string;
    /** 'required' = note mandatory; 'optional' = note shown but skippable;
     *  'none' = no further details needed, so the field is hidden entirely. */
    details: 'required' | 'optional' | 'none';
    /** Single heroicons-outline path (matches the design mock). */
    icon: string;
}

// Outline icon paths lifted from the design mock so the modal stays faithful.
const ICON = {
    pause: 'M15.75 5.25v13.5m-7.5-13.5v13.5',
    check: 'm4.5 12.75 6 6 9-13.5',
    x: 'M6 18 18 6M6 6l12 12',
    arrowRight: 'M4.5 12h15m0 0-6-6m6 6-6 6',
    chevronLeft: 'm15 18-6-6 6-6'
} as const;

const REASONS: ReasonMeta[] = [
    {
        id: 'records-incomplete',
        label: 'Records incomplete',
        help: 'Missing or low-quality records',
        details: 'required',
        field: 'What records are missing?',
        placeholder: 'List the records that need to be re-taken…',
        icon: 'M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z'
    },
    {
        id: 'clinical-question',
        label: 'Clinical question',
        help: 'Need input from the treating doctor',
        details: 'required',
        field: 'Question for the doctor',
        placeholder: 'Type your clinical question to GP…',
        icon: 'M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z'
    },
    {
        id: 'in-office-step',
        label: 'In-office step needed',
        help: 'Requires an in-person appointment',
        details: 'required',
        field: 'Describe the in-office step',
        placeholder: 'Describe to GP why an in-office appointment is needed…',
        icon: 'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5'
    },
    {
        id: 'refinement-needed',
        label: 'Refinement needed',
        help: 'Put the case on hold for refinement',
        details: 'none',
        field: 'Refinement details',
        placeholder: '',
        icon: 'M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75'
    },
    {
        id: 'retention-needed',
        label: 'Retention needed',
        help: 'Put the case on hold for retention',
        details: 'none',
        field: 'Retention notes',
        placeholder: '',
        icon: 'M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z'
    }
];

function Icon({
    d,
    size = 16,
    sw = 1.6,
    className
}: {
    d: string;
    size?: number;
    sw?: number;
    className?: string;
}) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={sw}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <path d={d} />
        </svg>
    );
}

// ── stepper ────────────────────────────────────────────────────────────────────
function Stepper({
    step,
    onBack,
    allowBack
}: {
    step: 1 | 2;
    onBack: () => void;
    allowBack: boolean;
}) {
    const segments: Array<[number, string]> = [
        [1, 'Reason'],
        [2, 'Details']
    ];
    return (
        <div className="flex gap-2 px-[22px] pb-3.5">
            {segments.map(([n, label]) => {
                const current = step === n;
                const done = step > n;
                const clickable = allowBack && n < step;
                return (
                    <button
                        key={n}
                        type="button"
                        onClick={() => clickable && onBack()}
                        className={`flex items-center gap-[7px] flex-1 ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                        <span
                            className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${
                                current || done
                                    ? 'bg-[rgb(217,119,6)] text-white'
                                    : 'bg-[rgb(238,239,243)] text-[rgb(140,146,158)]'
                            }`}
                        >
                            {done ? <Icon d={ICON.check} size={11} sw={3} /> : n}
                        </span>
                        <span
                            className={`text-xs ${
                                current
                                    ? 'font-bold text-gray-800'
                                    : done
                                      ? 'font-medium text-[rgb(120,126,138)]'
                                      : 'font-medium text-[rgb(173,178,189)]'
                            }`}
                        >
                            {label}
                        </span>
                        <span
                            className={`flex-1 h-0.5 rounded ${done ? 'bg-[rgb(217,119,6)]' : 'bg-[rgb(238,239,243)]'}`}
                        />
                    </button>
                );
            })}
        </div>
    );
}

// ── reason row ───────────────────────────────────────────────────────────────────
function ReasonRow({
    reason,
    selected,
    onSelect
}: {
    reason: ReasonMeta;
    selected: boolean;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onSelect}
            className={`flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-[10px] border-[1.5px] transition-colors ${
                selected
                    ? 'border-[rgb(180,100,20)] bg-[rgb(255,251,244)]'
                    : 'border-[rgb(233,234,238)] bg-white hover:border-gray-300'
            }`}
        >
            <span
                className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    selected ? 'bg-[rgb(254,243,224)]' : 'bg-gray-100'
                }`}
            >
                <Icon
                    d={reason.icon}
                    size={16}
                    className={selected ? 'text-[rgb(180,100,20)]' : 'text-[rgb(120,126,138)]'}
                />
            </span>
            <span className="flex-1 min-w-0">
                <span className="block text-[13.5px] font-semibold text-gray-800">
                    {reason.label}
                </span>
                <span className="block text-[11.5px] text-[rgb(140,146,158)] mt-px">
                    {reason.help}
                </span>
            </span>
            <span
                className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selected ? 'border-[rgb(180,100,20)]' : 'border-gray-300'
                }`}
            >
                {selected && <span className="w-2 h-2 rounded-full bg-[rgb(180,100,20)]" />}
            </span>
        </button>
    );
}

interface PutOnHoldModalProps {
    workCase: WorkQueueCase;
    onClose: () => void;
    /** Pre-select a reason instead of opening on the unselected picker. */
    initialReason?: HoldReason;
    /** Open directly on the details step; requires `initialReason`. */
    skipReasonStep?: boolean;
    /** Mirrors TaskBodyProps.onTaskComplete - lets an embedding task surface
     *  swap its now-stale body for the same CompletionCard other actions use. */
    onTaskComplete?: (action?: OutgoingAction) => void;
}

export function PutOnHoldModal({
    workCase: c,
    onClose,
    initialReason,
    skipReasonStep = false,
    onTaskComplete
}: PutOnHoldModalProps) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    // Set during submit, consumed once the success animation finishes -
    // keeps onTaskComplete's timing consistent with every other action.
    const completedActionRef = useRef<OutgoingAction | null>(null);

    const [reasonId, setReasonId] = useState<HoldReason | null>(initialReason ?? null);
    const [note, setNote] = useState('');
    const [noteDoc, setNoteDoc] = useState<TiptapDoc>(EMPTY_DOC);
    const [step, setStep] = useState<1 | 2>(initialReason && skipReasonStep ? 2 : 1);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const reason = useMemo(() => REASONS.find((r) => r.id === reasonId) ?? null, [reasonId]);
    // Clinical question / in-office step use the rich editor; other reasons use
    // a plain textarea - emptiness/length come from whichever is active.
    const isRich = reason?.id === 'clinical-question' || reason?.id === 'in-office-step';
    const richText = useMemo(() => tiptapDocToPlainText(noteDoc).trim(), [noteDoc]);
    const currentText = isRich ? richText : note.trim();
    const noteEmpty = currentText.length === 0;
    const overLimit = isRich && richText.length > MAX_HOLD_NOTE_LENGTH;
    const canContinue = !!reasonId;
    const canSubmit =
        !!reason && (reason.details !== 'required' || !noteEmpty) && !overLimit && !submitting;

    // Reset the note only when the reason actually changes, so returning to
    // step 1 and re-picking the same reason preserves what was typed.
    const handleSelectReason = useCallback((id: HoldReason) => {
        setReasonId((prev) => {
            if (prev !== id) {
                setNote('');
                setNoteDoc(EMPTY_DOC);
            }
            return id;
        });
    }, []);

    const handleImageUpload = useCallback(
        async (file: File) => {
            const result = await uploadInternalImage(c.id, file, 'work-queue-hold');
            return { url: result.url, fileId: result.fileId };
        },
        [c.id]
    );

    useEscapeKey(onClose, !submitting);

    // Auto-close after the success state, then hand off to onTaskComplete so
    // an embedding surface swaps its stale body for the CompletionCard.
    useEffect(() => {
        if (!done) return;
        const timer = setTimeout(() => {
            onTaskComplete?.(completedActionRef.current ?? undefined);
            onClose();
        }, 1100);
        return () => clearTimeout(timer);
    }, [done, onClose, onTaskComplete]);

    const handleSubmit = async (): Promise<void> => {
        if (!reason || !canSubmit) return;
        setSubmitting(true);
        try {
            const payload: Record<string, unknown> = { reason: reason.id };
            if (isRich) {
                const text = tiptapDocToPlainText(noteDoc).trim();
                if (text.length > 0) {
                    payload.note = text;
                    payload.noteDoc = noteDoc;
                }
            } else {
                const trimmedNote = note.trim();
                if (trimmedNote.length > 0) payload.note = trimmedNote;
            }

            await advanceWorkflow(c.id, 'enter-hold', payload);
            await invalidateCaseDetail(queryClient, c.id);
            if (onTaskComplete) {
                const fresh = await fetchCase(c.id).catch(() => null);
                completedActionRef.current = {
                    action: 'enter-hold',
                    label: 'Case put on hold',
                    actor: 'internal',
                    direction: 'lateral',
                    to: fresh?.statusId ?? c.statusId
                };
            }
            setDone(true);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            showToast('Failed to put case on hold. Please try again.', 'error');
            reportError({
                source: 'PutOnHoldModal.handleSubmit',
                message,
                stack: err instanceof Error ? err.stack : undefined,
                url: globalThis.location?.href ?? '',
                userAgent: navigator.userAgent,
                errorType: 'workflow_action_failure',
                metadata: { caseId: c.id, action: 'enter-hold', reason: reason.id }
            });
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6 font-inter">
            {/* Inert backdrop — dismiss only via Cancel / X / Escape, never a stray
          click-out, so an in-progress hold reason + note can't be lost. */}
            <div className="absolute inset-0 bg-[rgba(17,24,39,0.42)] backdrop-blur-[2px]" />
            <div className="relative w-[min(560px,100%)] max-h-[calc(100vh-48px)] bg-white rounded-2xl shadow-[0_24px_60px_rgba(0,0,0,0.28)] flex flex-col overflow-hidden">
                {done ? (
                    <div className="px-8 py-[52px] flex flex-col items-center gap-4 text-center">
                        <div className="w-14 h-14 rounded-full bg-[rgb(254,243,224)] flex items-center justify-center">
                            <Icon
                                d={ICON.check}
                                size={28}
                                sw={2.2}
                                className="text-[rgb(180,100,20)]"
                            />
                        </div>
                        <div>
                            <div className="text-lg font-bold text-[rgb(24,28,36)]">
                                Case put on hold
                            </div>
                            <div className="text-[13.5px] text-[rgb(120,126,138)] mt-1.5">
                                {reason?.label} · the team has been notified.
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="flex items-start gap-3 px-[22px] pt-5 pb-4">
                            <div className="w-[38px] h-[38px] rounded-[10px] bg-[rgb(254,243,224)] flex items-center justify-center flex-shrink-0">
                                <Icon
                                    d={ICON.pause}
                                    size={19}
                                    sw={2}
                                    className="text-[rgb(180,100,20)]"
                                />
                            </div>
                            <div className="flex-1 min-w-0 pt-px">
                                <div className="text-[17px] font-bold text-[rgb(24,28,36)] tracking-[-0.01em]">
                                    Put case on hold
                                </div>
                                <div className="text-[13px] text-[rgb(140,146,158)] mt-0.5 truncate">
                                    {c.name}
                                    {c.practice ? ` · ${c.practice}` : ''}
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                aria-label="Close"
                                className="w-[30px] h-[30px] rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-colors"
                            >
                                <Icon d={ICON.x} size={18} />
                            </button>
                        </div>

                        <Stepper
                            step={step}
                            onBack={() => setStep(1)}
                            allowBack={!skipReasonStep}
                        />

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto px-[22px] pb-2">
                            {/* Step 1 — pick a reason */}
                            {step === 1 && (
                                <div>
                                    <div className="text-[11px] font-bold text-[rgb(120,126,138)] tracking-[0.05em] mb-2.5">
                                        WHY IS THIS CASE GOING ON HOLD?
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        {REASONS.map((r) => (
                                            <ReasonRow
                                                key={r.id}
                                                reason={r}
                                                selected={reasonId === r.id}
                                                onSelect={() => handleSelectReason(r.id)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Step 2 — explanation */}
                            {step === 2 && reason && (
                                <div>
                                    {/* selected-reason recap → back to step 1 (read-only when skipReasonStep) */}
                                    {skipReasonStep ? (
                                        <div className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-[10px] border border-[rgb(238,239,243)] bg-[rgb(255,251,244)] mb-[18px]">
                                            <span className="w-7 h-7 rounded-[7px] bg-[rgb(254,243,224)] flex items-center justify-center flex-shrink-0">
                                                <Icon
                                                    d={reason.icon}
                                                    size={15}
                                                    className="text-[rgb(180,100,20)]"
                                                />
                                            </span>
                                            <span className="flex-1 min-w-0">
                                                <span className="block text-[10px] font-bold text-[rgb(180,130,60)] tracking-[0.05em]">
                                                    REASON
                                                </span>
                                                <span className="block text-[13.5px] font-semibold text-gray-800">
                                                    {reason.label}
                                                </span>
                                            </span>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setStep(1)}
                                            className="flex items-center gap-2.5 w-full text-left px-3 py-2.5 rounded-[10px] border border-[rgb(238,239,243)] bg-[rgb(255,251,244)] mb-[18px]"
                                        >
                                            <span className="w-7 h-7 rounded-[7px] bg-[rgb(254,243,224)] flex items-center justify-center flex-shrink-0">
                                                <Icon
                                                    d={reason.icon}
                                                    size={15}
                                                    className="text-[rgb(180,100,20)]"
                                                />
                                            </span>
                                            <span className="flex-1 min-w-0">
                                                <span className="block text-[10px] font-bold text-[rgb(180,130,60)] tracking-[0.05em]">
                                                    REASON
                                                </span>
                                                <span className="block text-[13.5px] font-semibold text-gray-800">
                                                    {reason.label}
                                                </span>
                                            </span>
                                            <span className="text-xs font-semibold text-[rgb(180,100,20)]">
                                                Change
                                            </span>
                                        </button>
                                    )}

                                    {reason.details === 'none' ? (
                                        <div className="flex items-center gap-2.5 px-3.5 py-3 rounded-[10px] bg-[rgb(250,250,251)] border border-[rgb(238,239,243)]">
                                            <Icon
                                                d={ICON.check}
                                                size={16}
                                                sw={2.2}
                                                className="text-[rgb(180,100,20)] flex-shrink-0"
                                            />
                                            <span className="text-[12.5px] text-[rgb(120,126,138)]">
                                                No additional details needed - this hold is ready to
                                                submit.
                                            </span>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="text-[11px] font-bold text-[rgb(120,126,138)] tracking-[0.05em] mb-2.5">
                                                {reason.field.toUpperCase()}
                                                {reason.details === 'required' ? (
                                                    <span className="text-[rgb(217,119,6)] ml-0.5">
                                                        *
                                                    </span>
                                                ) : (
                                                    <span className="font-medium text-[rgb(189,193,201)] tracking-normal ml-1.5">
                                                        · optional
                                                    </span>
                                                )}
                                            </div>
                                            {isRich ? (
                                                <>
                                                    <RichEditor
                                                        value={noteDoc}
                                                        onChange={setNoteDoc}
                                                        onImageUpload={handleImageUpload}
                                                        placeholder={reason.placeholder}
                                                        minHeightPx={180}
                                                    />
                                                    <div
                                                        className={`mt-1.5 text-[10px] text-right ${overLimit ? 'text-red-500' : 'text-[rgb(189,193,201)]'}`}
                                                    >
                                                        {richText.length}/{MAX_HOLD_NOTE_LENGTH}
                                                    </div>
                                                </>
                                            ) : (
                                                <textarea
                                                    key={reason.id}
                                                    value={note}
                                                    onChange={(e) => setNote(e.target.value)}
                                                    placeholder={reason.placeholder}
                                                    style={{ minHeight: 214 }}
                                                    className="w-full rounded-[10px] border-[1.5px] border-[rgb(233,234,238)] px-3.5 py-3 text-[13.5px] text-gray-800 placeholder-[rgb(173,178,189)] resize-none focus:outline-none focus:border-[rgb(217,119,6)] focus:ring-2 focus:ring-[rgba(217,119,6,0.15)] transition-colors"
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center gap-2.5 px-[22px] py-3.5 border-t border-[rgb(238,239,243)] bg-[rgb(250,250,251)]">
                            {step === 1 ? (
                                <>
                                    {!reasonId && (
                                        <span className="text-xs text-gray-400">
                                            Select a reason to continue
                                        </span>
                                    )}
                                    <div className="flex-1" />
                                    <button
                                        onClick={onClose}
                                        className="h-10 px-[18px] rounded-[9px] border border-[rgb(216,219,224)] bg-white text-[13.5px] font-semibold text-[rgb(75,82,96)] hover:bg-gray-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => canContinue && setStep(2)}
                                        disabled={!canContinue}
                                        className={`h-10 px-[22px] rounded-[9px] inline-flex items-center gap-2 text-[13.5px] font-bold text-white transition-colors ${
                                            canContinue
                                                ? 'bg-[rgb(24,28,36)] hover:bg-black'
                                                : 'bg-[rgb(224,226,230)] cursor-not-allowed'
                                        }`}
                                    >
                                        Continue
                                        <Icon d={ICON.arrowRight} size={15} sw={2.2} />
                                    </button>
                                </>
                            ) : (
                                <>
                                    {!skipReasonStep && (
                                        <button
                                            onClick={() => setStep(1)}
                                            disabled={submitting}
                                            className="h-10 pl-2.5 pr-3.5 rounded-[9px] inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[rgb(75,82,96)] hover:bg-gray-100 transition-colors disabled:opacity-50"
                                        >
                                            <Icon d={ICON.chevronLeft} size={16} sw={2.2} />
                                            Back
                                        </button>
                                    )}
                                    {reason?.details === 'required' && noteEmpty && (
                                        <span className="text-xs text-gray-400">
                                            Add an explanation to continue
                                        </span>
                                    )}
                                    <div className="flex-1" />
                                    <button
                                        onClick={() => void handleSubmit()}
                                        disabled={!canSubmit}
                                        className={`h-10 px-5 rounded-[9px] inline-flex items-center gap-2 text-[13.5px] font-bold text-white transition-colors ${
                                            canSubmit
                                                ? 'bg-[rgb(217,119,6)] hover:bg-[rgb(180,100,20)] shadow-[0_1px_2px_rgba(180,100,20,0.3)]'
                                                : 'bg-[rgb(245,222,179)] cursor-not-allowed'
                                        }`}
                                    >
                                        {submitting ? (
                                            <>
                                                <span className="h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                                Putting on hold…
                                            </>
                                        ) : (
                                            <>
                                                <Icon d={ICON.pause} size={14} sw={2.2} />
                                                Put on hold
                                            </>
                                        )}
                                    </button>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
